import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider
} from '@material-ui/core';

import DragArea from '../../../common/views/DragArea';
import {
  FileDto,
  ReferenceData,
  ReferenceMetadata
} from '../Document.interface';
import {
  DocumentFolderUploadList,
  FileUploadManager,
  FileUploadProgress,
  useFileUploadManager
} from './DocumentFolderUpload';

export interface DocumentMultiUploadProps {
  open: boolean;
  folder: FileDto;
  inputType?: string;
  referenceData?: ReferenceData;
  referenceTaskId?: number;
  referenceMetadata?: ReferenceMetadata;
  fileMultiInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  setOpen: (open: boolean) => void;
  onDismiss?: (progress: FileUploadProgress) => void;
  renderContent?: (manager: FileUploadManager) => React.ReactNode;
}

const DocumentMultiUpload: React.FC<DocumentMultiUploadProps> = (
  props: DocumentMultiUploadProps
) => {
  const {
    open,
    folder,
    inputType,
    referenceData,
    referenceTaskId,
    referenceMetadata,
    fileMultiInputRef: fileMultiInputRefProp,
    setOpen,
    onDismiss,
    renderContent
  } = props;
  const fileMultiInputRef = useRef<HTMLInputElement | null>(null);
  const cancelCallbackRef = useRef<(() => void) | undefined>();
  const [dragOver, setDragOver] = useState(false);
  const manager: FileUploadManager = useFileUploadManager({ unique: false });

  const dismiss = () => {
    cancelCallbackRef.current?.();
    setOpen(false);
    onDismiss?.(manager.progress);
  };

  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles && newFiles.length > 0) {
      // append new files
      manager.add(Array.from(newFiles));
    }
  };

  // clear state
  useEffect(() => {
    !open && manager.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <input
        hidden
        multiple
        type='file'
        accept={inputType}
        ref={(ref: HTMLInputElement | null) => {
          fileMultiInputRef.current = ref;
          if (fileMultiInputRefProp) {
            fileMultiInputRefProp.current = ref;
          }
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const { files: newFiles } = event.target;
          handleFiles(newFiles);
          // clear value
          if (fileMultiInputRef.current) {
            fileMultiInputRef.current.value = '';
          }
        }}
      />

      <Dialog open={open} onClose={dismiss}>
        <DialogTitle>Document Multiple Upload</DialogTitle>
        {renderContent?.(manager)}
        <Box mx={3}>
          <DragArea
            dragOver={dragOver}
            onUpload={handleFiles}
            setDragOver={setDragOver}
            onClick={() => fileMultiInputRef.current?.click()}
          />
        </Box>
        <Collapse in={manager.items.length > 0} style={{ minHeight: 'unset' }}>
          <Box mt={2}>
            <Box mb={2}>
              <Divider />
            </Box>
            <DocumentFolderUploadList
              folder={folder}
              manager={manager}
              referenceData={referenceData}
              referenceTaskId={referenceTaskId}
              referenceMetadata={referenceMetadata}
              renderActions={({ progress, uploadAllButton, cancelAll }) => {
                cancelCallbackRef.current = cancelAll;
                return (
                  <DialogActions>
                    <Button
                      variant='outlined'
                      onClick={dismiss}
                      disabled={progress.uploading > 0}
                    >
                      Dismiss
                    </Button>
                    {uploadAllButton}
                  </DialogActions>
                );
              }}
            />
          </Box>
        </Collapse>
        {manager.items.length === 0 && (
          <DialogActions>
            <Button variant='outlined' onClick={dismiss}>
              Dismiss
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default DocumentMultiUpload;
